// The scope dropdowns, served from the rollup payload instead of raw rows.
//
// These lists need identity columns only, and the payload already carries them
// UNSCOPED -- it has to, because the NVA benchmark is every eligible site. So
// this costs no extra fetch.
//
// Built from the doctor-week rows rather than the site-week ones because the
// dropdowns cross-filter: choosing a DVM narrows the site list to where that
// doctor works, and site-week rows have no resource_id to answer that with. It
// also matches the raw path's grain -- a raw row is a doctor-site-day, so a site
// appears there only if a doctor worked it, and deriving sites from site-weeks
// would offer sites the raw path never did.
//
// location_name is the one identity column the narrow projection omits, so it is
// looked up from the site-week rows, which carry it for every site.

import { useMemo } from "react";
import type { ScopeFilters, ScopeOptions } from "@/types/filters";
import {
  buildScopeCounts,
  buildScopeOptions,
  type ScopeCounts,
  type ScopeIdentityRow,
} from "@/lib/scopeOptions";
import type { RollupPayload } from "@/hooks/useRollupData";

// The identity columns of every row in a payload, in one list. Exported because
// the scope dropdowns, the header counts and the reverse-fill lookup all need the
// same thing, and building it three times would be three chances to build it
// differently.
export function identityRowsFromPayload(payload: RollupPayload): ScopeIdentityRow[] {
  const siteNames = new Map<string, string>();
  for (const s of payload.siteWeeks) {
    siteNames.set(String(s.location_id), String(s.location_name ?? ""));
  }

  const identity: ScopeIdentityRow[] = payload.dvmWeeks.map((d) => ({
    country_code: String(d.country_code ?? ""),
    group_name: String(d.group_name ?? ""),
    division_name: String(d.division_name ?? ""),
    location_id: String(d.location_id ?? ""),
    location_name: siteNames.get(String(d.location_id)) ?? "",
    resource_id: String(d.resource_id ?? ""),
    resource_full_name: String(d.resource_full_name ?? ""),
  }));

  // The ragged edge is raw rows, which already carry every identity column.
  for (const r of payload.edgeRows) {
    identity.push({
      country_code: r.country_code,
      group_name: r.group_name,
      division_name: r.division_name,
      location_id: r.location_id,
      location_name: r.location_name,
      resource_id: r.resource_id,
      resource_full_name: r.resource_full_name,
    });
  }

  return identity;
}

export function useRollupScopeOptions(
  payload: RollupPayload | null,
  scope: ScopeFilters
): { options: ScopeOptions; counts: ScopeCounts; identity: ScopeIdentityRow[] } | null {
  return useMemo(() => {
    if (!payload) return null;
    const identity = identityRowsFromPayload(payload);
    return {
      options: buildScopeOptions(identity, scope),
      counts: buildScopeCounts(identity, scope),
      identity,
    };
  }, [payload, scope]);
}
